class CartRecommendations extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.cart-recommendations__track');
    this.viewport = this.querySelector('.cart-recommendations__viewport');
    this.previousButton = this.querySelector('.cart-recommendations__arrow--previous');
    this.nextButton = this.querySelector('.cart-recommendations__arrow--next');
    this.productId = this.dataset.productId;

    this.previousButton?.addEventListener('click', () => this.move(-1));
    this.nextButton?.addEventListener('click', () => this.move(1));
    this.track.addEventListener('click', (event) => this.addRecommendedProduct(event));
    this.addEventListener('pointerenter', () => this.pause());
    this.addEventListener('pointerleave', () => this.resume());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', () => this.resume());
    document.addEventListener('visibilitychange', () => (document.hidden ? this.pause() : this.resume()));

    if (this.productId) this.load(this.productId);
  }

  async load(productId) {
    if (!productId || this.loadingProductId === String(productId)) return;
    this.loadingProductId = String(productId);
    this.pause();
    this.hidden = true;
    this.track.replaceChildren();

    try {
      const response = await fetch(`/recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=5&intent=related`);
      if (!response.ok) throw new Error('Recommendation request failed');
      const { products = [] } = await response.json();
      const uniqueProducts = products.filter((product, index, all) => all.findIndex((item) => item.id === product.id) === index).slice(0, 5);
      if (!uniqueProducts.length) return;

      uniqueProducts.forEach((product) => this.track.append(this.createCard(product)));
      this.hidden = false;
      this.updateArrows();
      this.resume();
    } catch (error) {
      console.warn('Unable to load cart recommendations.', error);
    }
  }

  createCard(product) {
    const variant = product.variants?.find((item) => item.available) || product.variants?.[0];
    const card = document.createElement('article');
    card.className = 'cart-recommendations__card';
    card.setAttribute('role', 'listitem');

    const imageLink = document.createElement('a');
    imageLink.className = 'cart-recommendations__image-link';
    imageLink.href = product.url || `/products/${product.handle}`;
    const image = document.createElement('img');
    image.src = typeof product.featured_image === 'string' ? product.featured_image : product.featured_image?.url;
    image.alt = product.title;
    image.width = 144;
    image.height = 144;
    image.loading = 'lazy';
    imageLink.append(image);

    const details = document.createElement('div');
    details.className = 'cart-recommendations__details';
    const title = document.createElement('a');
    title.className = 'cart-recommendations__title';
    title.href = product.url || `/products/${product.handle}`;
    title.textContent = product.title;
    const price = document.createElement('p');
    price.className = 'cart-recommendations__price';
    const formatPrice = new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      maximumFractionDigits: 0,
    });
    const currentPrice = Number(variant?.price || product.price || 0);
    const compareAtPrice = Number(variant?.compare_at_price || product.compare_at_price || 0);

    if (compareAtPrice > currentPrice) {
      const originalPrice = document.createElement('s');
      originalPrice.className = 'cart-recommendations__original-price';
      originalPrice.textContent = formatPrice.format(compareAtPrice / 100);
      const salePrice = document.createElement('span');
      salePrice.className = 'cart-recommendations__sale-price';
      salePrice.textContent = formatPrice.format(currentPrice / 100);
      price.append(originalPrice, salePrice);
    } else {
      price.textContent = formatPrice.format(currentPrice / 100);
    }
    details.append(title, price);

    const addButton = document.createElement('button');
    addButton.className = 'cart-recommendations__add';
    addButton.type = 'button';
    addButton.textContent = variant?.available ? '+  AGREGAR' : 'AGOTADO';
    addButton.disabled = !variant?.available;
    if (variant?.id) addButton.dataset.variantId = variant.id;
    addButton.dataset.productId = product.id;

    card.append(imageLink, details, addButton);
    return card;
  }

  move(direction) {
    const card = this.track.querySelector('.cart-recommendations__card');
    if (!card) return;
    const gap = Number.parseFloat(getComputedStyle(this.track).gap) || 0;
    const distance = card.getBoundingClientRect().width + gap;
    const max = this.track.scrollWidth - this.track.clientWidth;
    const next = this.track.scrollLeft + direction * distance;
    this.track.scrollTo({ left: next < 0 ? max : next > max - 4 ? 0 : next, behavior: 'smooth' });
  }

  resume() {
    if (this.timer || this.hidden || document.hidden || this.track.children.length < 2) return;
    this.timer = window.setInterval(() => this.move(1), 3000);
  }

  pause() {
    window.clearInterval(this.timer);
    this.timer = null;
  }

  updateArrows() {
    const hasMultipleProducts = this.track.children.length > 1;
    if (this.previousButton) this.previousButton.hidden = !hasMultipleProducts;
    if (this.nextButton) this.nextButton.hidden = !hasMultipleProducts;
  }

  async addRecommendedProduct(event) {
    const button = event.target.closest('[data-variant-id]');
    if (!button || button.disabled) return;
    button.disabled = true;
    button.textContent = 'AGREGANDO...';

    try {
      const cartNotification = this.dataset.surface === 'notification' ? document.querySelector('cart-notification') : null;
      const cartItems = this.dataset.surface === 'cart' ? document.querySelector('cart-items') : null;
      const sections = cartNotification
        ?.getSectionsToRender?.()
        .map((section) => section.id) || cartItems?.getSectionsToRender?.().map((section) => section.section);
      const payload = { items: [{ id: Number(button.dataset.variantId), quantity: 1 }] };
      if (sections?.length) {
        payload.sections = sections.join(',');
        payload.sections_url = window.location.pathname;
      }

      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Cart add failed');
      const cartData = await response.json();
      if (cartData.status) throw new Error(cartData.description || 'Cart add failed');
      button.textContent = 'AGREGADO';
      if (cartNotification && cartData.sections) {
        cartNotification.renderContents({
          ...cartData,
          product_id: cartData.product_id || button.dataset.productId,
        });
      } else if (cartItems && cartData.sections) {
        this.renderCartPage(cartItems, cartData);
      } else {
        await this.syncCartCount();
      }
    } catch (error) {
      button.disabled = false;
      button.textContent = '+  AGREGAR';
      console.warn('Unable to add recommended product.', error);
    }
  }

  renderCartPage(cartItems, cartData) {
    cartItems.getSectionsToRender().forEach((section) => {
      const sectionElement = document.getElementById(section.id);
      const sectionMarkup = cartData.sections[section.section];
      if (!sectionElement || !sectionMarkup) return;

      const target = sectionElement.querySelector(section.selector) || sectionElement;
      target.innerHTML = cartItems.getSectionInnerHTML(sectionMarkup, section.selector);
    });
  }

  async syncCartCount() {
    const response = await fetch(`${window.routes.cart_url}.js`);
    if (!response.ok) return;

    const cart = await response.json();
    const count = Number(cart.item_count) || 0;
    const notificationButton = document.getElementById('cart-notification-button');

    if (notificationButton) {
      notificationButton.textContent = `Ver carrito (${count})`;
    }

    document.querySelectorAll('#cart-icon-bubble .cart-count-bubble > span[aria-hidden="true"]').forEach((bubble) => {
      bubble.textContent = count;
    });
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
